import {
  GetBillingCycle,
  GetClientFeatures,
  GetClientSettings,
  GetOverdueSummary,
  PostAcceptScrape,
  PutClientSettings,
  PutProductAnchorDate,
} from "@/server-types";
import { useCustomQuery } from "../custom-query";
import { get, post, put } from "../request-helpers";
import { useCustomMutation } from "../custom-mutation";
import { queryClient } from "..";

/**
 * GET /Client/feature
 */
export const useGetClientFeatures = (isEnabled: boolean = true) => {
  return useCustomQuery({
    queryKey: ["clientFeatures"],
    queryFn: () => get<GetClientFeatures>("/client/feature"),
    staleTime: 1000 * 60 * 60 * 24, // 24 hours (still refetches on refresh)
    displayNotification: true,
    enabled: isEnabled,
  });
};

/**
 * GET /analytics/revenue
 */
export const useBillingCycle = () => {
  return useCustomQuery({
    queryKey: ["analyticsRevenue"],
    queryFn: () => get<GetBillingCycle>("/user/billing_cycle"),
  });
};

/**
 * GET /client/billing/overdue-summary
 */
export const useOverdueSummary = () => {
  return useCustomQuery({
    queryKey: ["overdueSummary"],
    queryFn: () => get<GetOverdueSummary>("/client/billing/overdue-summary"),
    staleTime: 1000 * 60 * 5, // 5 min
  });
};

/**
 * GET /settings
 */
export const useClientSettings = ({ enabled }: { enabled?: boolean } = {}) => {
  return useCustomQuery({
    queryKey: ["clientSettings"],
    queryFn: () => get<GetClientSettings>("/client/settings"),
    enabled: enabled ?? true,
  });
};

/**
 * PUT /settings
 */
export const usePutClientSettings = () => {
  return useCustomMutation({
    mutationFn: (body: PutClientSettings["body"]) =>
      put<PutClientSettings>("/client/settings", body),
  });
};

/**
 * PUT /settings/product-anchor-date
 */
export const usePutProductAnchorDate = () => {
  return useCustomMutation({
    mutationFn: (body: PutProductAnchorDate["body"]) =>
      put<PutProductAnchorDate>("/client/settings/product-anchor-date", body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clientSettings"] });
    },
  });
};

/**
 * POST /client/accept-scrape
 */
export const useAcceptScrape = ({
  onSuccess,
}: {
  onSuccess?: () => void;
} = {}) => {
  return useCustomMutation({
    mutationFn: () => post<PostAcceptScrape>("/client/accept-scrape", {}),
    onSuccess,
  });
};
